import { FC, useEffect } from 'react';
import { BusinessUnitModel } from 'api/model';
import Modal from 'components/Modal';
import { useForm } from 'react-hook-form';

import styled from 'styled-components';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { arraySpreadIf } from 'utils/spreading';
import LabelWrap from 'components/inputs/LabelWrap';
import Input from 'components/inputs/Input';
import TextArea from 'components/inputs/TextArea';
import Checkbox from 'components/inputs/Checkbox';
import {
  useBusinessUnitCreateBusinessUnitHook,
  useBusinessUnitGetBusinessUnitGroups,
  useBusinessUnitUpdateBusinessUnitHook,
} from 'api/business-unit/business-unit';
import Select from 'components/inputs/Select';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';

const FormWrap = styled.div<{ maxWidth?: number; compact?: boolean }>`
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => (compact ? '0' : '10px')};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px;` : '')}
`;

const MyLabelWrap = styled(LabelWrap)<{ width?: number }>`
  & > *:last-child {
    width: ${({ width }) => (width ? `${width}px` : 'auto')};
    max-height: 100%;
  }
`;

interface Props {
  unit?: BusinessUnitModel;
  onCancel?(): void;
  onSaved?(): void;
  canEdit?: boolean;
  title?: string;
}

const BusinessUnitFormModal: FC<Props> = ({
  unit,
  onCancel,
  onSaved,
  canEdit,
  title,
}) => {
  const me = useMe();
  const isSuperAdmin = me?.roles
    ? includesRoles([Roles.SuperAdmin], me.roles)
    : false;
  const { register, handleSubmit, setValue } = useForm<BusinessUnitModel>({
    defaultValues: {
      isActive: true,
      includeInHogiaSalaryExport: false,
    },
  });

  const businessUnitGroups = useBusinessUnitGetBusinessUnitGroups();

  // Populate form with area data on first render
  useEffect(() => {
    if (unit) {
      setValue('id', unit.id);
      setValue('name', unit.name);
      setValue('description', unit.description);
      setValue('isActive', unit.isActive);
      setValue('businessUnitGroupID', unit.businessUnitGroupID);
      setValue('averageSalaryPerHour', unit.averageSalaryPerHour);
      setValue('obPerHour', unit.obPerHour);
      setValue('includeInHogiaSalaryExport', unit.includeInHogiaSalaryExport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const createMode = !unit;

  const createBusinessUnit = useBusinessUnitCreateBusinessUnitHook();
  const updateBusinessUnit = useBusinessUnitUpdateBusinessUnitHook();

  const createCall = useApiCallFn((model: BusinessUnitModel) =>
    createBusinessUnit(model)
  );

  const updateCall = useApiCallFn((model: BusinessUnitModel) =>
    updateBusinessUnit(model)
  );

  const handleCreateClick = handleSubmit(async (data) => {
    const [, error] = await createCall.run(data);

    if (!error) {
      onSaved?.();
    }
  });

  const handleUpdateClick = handleSubmit(async (data) => {
    if (!data) return;

    const [, error] = await updateCall.run(data);

    if (!error) {
      onSaved?.();
      businessUnitGroups.refetch();
    }
  });

  const handleCancelClick = () => {
    onCancel?.();
  };

  const saveStatus = createMode ? createCall.status : updateCall.status;

  const renderContent = () => {
    switch (saveStatus) {
      case RequestStatus.Idle:
      case RequestStatus.Fetched:
      case RequestStatus.Error:
        return (
          <FormWrap>
            <MyLabelWrap label="Namn" width={270}>
              <Input
                {...register('name', { required: true })}
                disabled={!canEdit}
                placeholder="Namn"
              />
            </MyLabelWrap>
            <MyLabelWrap label="Beskrivning" width={270}>
              <TextArea
                {...register('description', { required: true })}
                disabled={!canEdit}
                placeholder="Beskrivning"
              />
            </MyLabelWrap>
            <MyLabelWrap label="Status" width={270}>
              <Checkbox {...register('isActive', { required: true })}>
                Aktiv
              </Checkbox>
            </MyLabelWrap>
            <MyLabelWrap label="Enhetsgrupp" width={270}>
              <Select
                {...register('businessUnitGroupID', { required: true })}
                disabled={!canEdit}
              >
                <option value="">-- Välj enhetsgrupp --</option>
                {businessUnitGroups.data?.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </MyLabelWrap>
            <MyLabelWrap label="Löneexport" width={270}>
              <Checkbox
                {...register('includeInHogiaSalaryExport')}
                disabled={!canEdit}
              >
                Inkludera i löneexporten till Hogia
              </Checkbox>
            </MyLabelWrap>
            {isSuperAdmin && (
              <MyLabelWrap label="Genomsnittslön per timme" width={270}>
                <Input
                  {...register('averageSalaryPerHour', {
                    required: false,
                    valueAsNumber: true,
                    min: 0,
                  })}
                  disabled={!canEdit}
                  placeholder="227"
                  type="number"
                />
              </MyLabelWrap>
            )}
            {isSuperAdmin && (
              <MyLabelWrap label="OB per timme" width={270}>
                <Input
                  {...register('obPerHour', {
                    required: false,
                    valueAsNumber: true,
                    min: 0,
                  })}
                  disabled={!canEdit}
                  placeholder="45"
                  type="number"
                />
              </MyLabelWrap>
            )}
          </FormWrap>
        );

      case RequestStatus.Fetching:
      default:
        return <LoadingSpinner>Sparar...</LoadingSpinner>;
    }
  };

  return (
    <Modal
      buttons={
        saveStatus === RequestStatus.Fetching
          ? []
          : [
              ...arraySpreadIf(!!canEdit, {
                label: createMode ? 'Skapa enhet' : 'Uppdatera enhet',
                onClick: createMode ? handleCreateClick : handleUpdateClick,
              }),
              {
                label: canEdit ? 'Avbryt' : 'Stäng',
                onClick: handleCancelClick,
              },
            ]
      }
      onClose={handleCancelClick}
      title={
        title !== undefined
          ? title
          : createMode
            ? 'Skapa enhet'
            : 'Redigera enhet'
      }
    >
      {renderContent()}
    </Modal>
  );
};

export default BusinessUnitFormModal;
